import { useEffect, useState } from 'react'
import { Button, Checkbox, CheckboxGroup, Popover, ToggleButton, ToggleButtonGroup } from '@heroui/react'
import { latestRead } from '../../lib/latestRead'
import { Icon } from '../../ui/icons'
import { useStaffText } from '../../ui/StaffText'
import { Find } from '../kit'
import { clearFieldFilter, DATE_GROUPS, setDateGroup, setKeep } from './layout'
import type { PivotSpec } from './types'

export interface FunnelProps {
  field: string
  label: string
  layout: PivotSpec
  onChange: (layout: PivotSpec) => void
  values: (field: string, signal: AbortSignal) => Promise<string[]>
  count: number
  readOnly?: boolean
}

/** The funnel on a placed dimension: search, select all / clear, the values in scope; Date also groups. */
export function Funnel({ field, label, layout, onChange, values, count, readOnly }: FunnelProps) {
  const { say } = useStaffText()
  const [open, setOpen] = useState(false)
  return (
    <Popover isOpen={open} onOpenChange={setOpen}>
      <Button isIconOnly size="sm" variant="ghost" className="rp-ic" data-on={count ? '' : undefined} aria-label={say('pivot.filter', { field: label })} isDisabled={readOnly}>
        <Icon name="filter" size={13} />
        {count > 0 && <b>{count}</b>}
      </Button>
      <Popover.Content placement="bottom start" className="rp-pop">
        <Popover.Dialog aria-label={say('pivot.filter', { field: label })}>
          {open && (
            <FunnelBody
              field={field}
              label={label}
              layout={layout}
              values={values}
              onClose={() => setOpen(false)}
              onApply={(next) => {
                setOpen(false)
                onChange(next)
              }}
            />
          )}
        </Popover.Dialog>
      </Popover.Content>
    </Popover>
  )
}

function FunnelBody({
  field,
  label,
  layout,
  values,
  onClose,
  onApply,
}: {
  field: string
  label: string
  layout: PivotSpec
  values: (field: string, signal: AbortSignal) => Promise<string[]>
  onClose: () => void
  onApply: (layout: PivotSpec) => void
}) {
  const { say } = useStaffText()
  const [all, setAll] = useState<string[] | null>(null)
  const [failed, setFailed] = useState(false)
  const [selected, setSelected] = useState<Set<string> | null>(null)
  const [search, setSearch] = useState('')
  const group = field === 'date' ? layout.dateGroup : ''

  useEffect(() => {
    setAll(null)
    return latestRead((signal) => values(field, signal), {
      done: (v) => setAll(v),
      failed: () => setFailed(true),
    })
  }, [field, group]) // eslint-disable-line react-hooks/exhaustive-deps

  const list = all ?? []
  const kept = layout.keep[field] ?? []
  const excluded = new Set(layout.excl[field] ?? [])
  const current = selected ?? new Set(list.filter((v) => (kept.length ? kept.includes(v) : true) && !excluded.has(v)))
  const shown = list.filter((v) => v.toLocaleLowerCase().includes(search.toLocaleLowerCase()))

  const apply = () => {
    const chosen = list.filter((v) => current.has(v))
    // Every value ticked is no filter at all.
    const keep = chosen.length === list.length ? [] : chosen
    onApply(setKeep(clearFieldFilter(layout, field), field, keep))
  }

  return (
    <div className="rp-funnel">
      <div className="rp-pop-t">{label}</div>
      {field === 'date' && (
        <>
          <div className="rp-pop-l">{say('pivot.groupBy')}</div>
          <ToggleButtonGroup
            className="rp-grp"
            aria-label={say('pivot.groupBy')}
            size="sm"
            fullWidth
            selectionMode="single"
            disallowEmptySelection
            selectedKeys={[layout.dateGroup]}
            onSelectionChange={(keys) => {
              const [next] = [...keys]
              if (next !== undefined && next !== layout.dateGroup) onApply(setDateGroup(layout, String(next) as PivotSpec['dateGroup']))
            }}
          >
            {DATE_GROUPS.map((g) => (
              <ToggleButton key={g.value} id={g.value}>
                {say(g.key)}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </>
      )}
      <Find fullWidth className="rp-funnel__find" label={say('pivot.searchValues')} value={search} onChange={setSearch} />
      <div className="rp-funnel__bulk">
        <Button variant="ghost" size="sm" onPress={() => setSelected(new Set(list))}>
          {say('pivot.selectAll')}
        </Button>
        <Button variant="ghost" size="sm" onPress={() => setSelected(new Set())}>
          {say('pivot.clear')}
        </Button>
      </div>
      {failed ? (
        <p className="rp-muted">{say('pivot.valuesFailed')}</p>
      ) : all === null ? (
        <p className="rp-muted">{say('pivot.loading')}</p>
      ) : (
        <CheckboxGroup className="rp-vlist" aria-label={label} value={[...current]} onChange={(v) => setSelected(new Set(v))}>
          {shown.length === 0 && <p className="rp-muted">{say('pivot.noValues')}</p>}
          {shown.map((v) => (
            <Checkbox key={v} value={v} className="rp-tick">
              <Checkbox.Content>
                <Checkbox.Control>
                  <Checkbox.Indicator />
                </Checkbox.Control>
                {v}
              </Checkbox.Content>
            </Checkbox>
          ))}
        </CheckboxGroup>
      )}
      <div className="rp-pop-acts">
        <Button variant="tertiary" size="sm" onPress={onClose}>
          {say('common.cancel')}
        </Button>
        <Button variant="primary" size="sm" isDisabled={all === null} onPress={apply}>
          {say('pivot.apply')}
        </Button>
      </div>
    </div>
  )
}
